import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowLeft, Save, Loader2, Plus, PlayCircle, Calendar, Tag, Clock,
  MessageCircle, Send, Flag, GitBranch, AlertCircle, Smartphone,
} from 'lucide-react';
import WhatsAppSimulator from './WhatsAppSimulator';
import { toast } from 'react-toastify';
import {
  workflowsApi, Workflow, Step, StepType,
} from '../../../../services/workflowsApi';
import WorkflowStepCard from './WorkflowStepCard';

interface WorkflowEditorProps {
  clientId: string;
  workflowId: string;
  onClose: () => void;
}

export const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  clientId,
  workflowId,
  onClose,
}) => {
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const wf = await workflowsApi.get(clientId, workflowId);
      setWorkflow(wf);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load workflow');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [clientId, workflowId, onClose]);

  useEffect(() => { load(); }, [load]);

  if (loading || !workflow) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
        <Loader2 size={18} className="animate-spin inline-block mr-2" />
        Loading workflow…
      </div>
    );
  }

  const wf = workflow;

  const updateLocal = (patch: Partial<Workflow>) => setWorkflow({ ...wf, ...patch });
  const updateDefinition = (patch: Partial<Workflow['definition']>) =>
    setWorkflow({ ...wf, definition: { ...wf.definition, ...patch } });
  const updateTrigger = (patch: Partial<Workflow['trigger']>) =>
    setWorkflow({ ...wf, trigger: { ...wf.trigger, ...patch } });

  // -- step ops ------------------------------------------------------------

  const setStep = (i: number, next: Step) => {
    const steps = [...wf.definition.steps];
    steps[i] = next;
    updateDefinition({ steps });
  };
  const removeStep = (i: number) => {
    if (!window.confirm('Remove this step?')) return;
    const steps = wf.definition.steps.filter((_, idx) => idx !== i);
    updateDefinition({ steps });
  };
  const moveStep = (i: number, dir: -1 | 1) => {
    const steps = [...wf.definition.steps];
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    updateDefinition({ steps });
  };
  const addStep = (type: StepType) => {
    const id = generateStepId(wf.definition.steps, type);
    const step = makeBlankStep(type, id);
    updateDefinition({ steps: [...wf.definition.steps, step] });
  };

  // -- save / run ----------------------------------------------------------

  const save = async (alsoActivate?: boolean) => {
    setSaving(true);
    try {
      const updated = await workflowsApi.update(clientId, wf.id, {
        name: wf.name,
        description: wf.description || undefined,
        trigger: wf.trigger,
        definition: wf.definition,
        active: alsoActivate ?? wf.active,
        bump_version: true,
      });
      setWorkflow(updated);
      toast.success(alsoActivate ? 'Saved & activated' : 'Saved');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await workflowsApi.runNow(clientId, wf.id);
      toast.success(`Enrolled ${res.enrolled} ${res.enrolled === 1 ? 'recipient' : 'recipients'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSimulatorOpen(true)}
            title="Walk through this workflow without sending real WhatsApp messages"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100 whitespace-nowrap"
          >
            <Smartphone size={14} />
            Test
          </button>
          <button
            onClick={runNow}
            disabled={running || !wf.active}
            title={wf.active ? 'Run for matching audience now' : 'Activate to enable run'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            Run now
          </button>
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save draft
          </button>
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save & activate
          </button>
        </div>
      </div>

      {/* Identity */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Workflow name</label>
          <input
            value={wf.name}
            onChange={(e) => updateLocal({ name: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-base font-medium focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <textarea
            value={wf.description || ''}
            onChange={(e) => updateLocal({ description: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            placeholder="What does this workflow do?"
          />
        </div>
      </div>

      {/* Trigger */}
      <TriggerSection trigger={wf.trigger} onChange={updateTrigger} />

      {/* Steps */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h5 className="font-medium text-gray-900 text-sm">Steps</h5>
          <span className="text-xs text-gray-500">
            {wf.definition.steps.length} {wf.definition.steps.length === 1 ? 'step' : 'steps'}
          </span>
        </div>

        {wf.definition.steps.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 border-dashed p-6 text-center text-sm text-gray-500">
            No steps yet — add one below.
          </div>
        ) : (
          <div className="space-y-2">
            {wf.definition.steps.map((step, i) => (
              <WorkflowStepCard
                key={step.id + i}
                clientId={clientId}
                step={step}
                index={i}
                total={wf.definition.steps.length}
                onChange={(next) => setStep(i, next)}
                onRemove={() => removeStep(i)}
                onMoveUp={() => moveStep(i, -1)}
                onMoveDown={() => moveStep(i, 1)}
              />
            ))}
          </div>
        )}

        <AddStepRow onAdd={addStep} />
      </div>

      {/* Missed-response policy */}
      <MissedResponseSection
        policy={wf.definition.missed_response_policy}
        onChange={(p) => updateDefinition({ missed_response_policy: p })}
      />

      {/* Simulator — opens in bottom-right when "Test" is clicked */}
      {simulatorOpen && (
        <WhatsAppSimulator
          workflow={wf}
          clientId={clientId}
          businessName={wf.name}
          onClose={() => setSimulatorOpen(false)}
        />
      )}
    </div>
  );
};

// -- Trigger section --------------------------------------------------------

const TriggerSection: React.FC<{
  trigger: Workflow['trigger'];
  onChange: (patch: Partial<Workflow['trigger']>) => void;
}> = ({ trigger, onChange }) => {
  const [tagsInput, setTagsInput] = useState((trigger.audience?.tags || []).join(', '));

  const updateAudience = (patch: Partial<NonNullable<Workflow['trigger']['audience']>>) =>
    onChange({ audience: { ...(trigger.audience || {}), ...patch } });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h5 className="font-medium text-gray-900 text-sm">Trigger</h5>
        <span className="text-xs text-gray-400">When does this workflow start?</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {(['schedule', 'manual', 'event'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onChange({ type: t })}
            className={`px-3 py-2 rounded-lg text-sm border transition-all capitalize ${
              trigger.type === t
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 ring-2 ring-emerald-100'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t === 'schedule' ? <Calendar size={13} className="inline mr-1.5" /> : null}
            {t === 'event' ? <AlertCircle size={13} className="inline mr-1.5" /> : null}
            {t === 'manual' ? <PlayCircle size={13} className="inline mr-1.5" /> : null}
            {t}
          </button>
        ))}
      </div>

      {trigger.type === 'schedule' && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
            <select
              value={trigger.per_recipient_field === 'preferred_time_local' ? 'per_recipient' : 'cron'}
              onChange={(e) =>
                e.target.value === 'per_recipient'
                  ? onChange({
                      per_recipient_field: 'preferred_time_local',
                      timezone_field: 'timezone',
                      cron: undefined,
                    })
                  : onChange({
                      per_recipient_field: undefined,
                      timezone_field: undefined,
                      cron: '0 9 * * *',
                      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                    })
              }
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <option value="per_recipient">At each recipient's preferred time</option>
              <option value="cron">Same time for everyone</option>
            </select>
          </div>

          {trigger.per_recipient_field !== 'preferred_time_local' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Repeat</label>
                <select
                  value={cronWeekday(trigger.cron) ?? 'daily'}
                  onChange={(e) => onChange({
                    cron: withWeekday(trigger.cron, e.target.value === 'daily' ? null : e.target.value),
                    timezone: trigger.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                  })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="daily">Every day</option>
                  {WEEKDAYS.map((day, i) => (
                    <option key={day} value={String(i)}>Every week on {day}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Send at</label>
                <input
                  type="time"
                  value={cronToLocalTime(trigger.cron)}
                  onChange={(e) => onChange({
                    cron: localTimeToCron(e.target.value, trigger.cron),
                    timezone: trigger.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                  })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  {scheduleLabel(trigger.cron)} at {humanTime(cronToLocalTime(trigger.cron))} ({trigger.timezone || 'UTC'}).
                </p>
              </div>
            </>
          )}
        </>
      )}

      {trigger.type === 'event' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Event</label>
          <select
            value={trigger.event || 'recipient.opted_in'}
            onChange={(e) => onChange({ event: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          >
            <option value="recipient.opted_in">Recipient opted in</option>
            <option value="recipient.created">Recipient added to roster</option>
            <option value="recipient.tag_added">Tag added</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          <Tag size={11} className="inline mr-1" />
          Audience (recipients with these tags)
        </label>
        <input
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          onBlur={() =>
            updateAudience({
              tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
            })
          }
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          placeholder="enrolled, cohort-a"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Leave blank to include everyone opted-in. Comma-separated. Recipients are matched if they have any of these tags.
        </p>
      </div>
    </div>
  );
};

// -- Missed response -------------------------------------------------------

const MissedResponseSection: React.FC<{
  policy: Workflow['definition']['missed_response_policy'];
  onChange: (next: Workflow['definition']['missed_response_policy']) => void;
}> = ({ policy, onChange }) => {
  const p = policy || {};
  const set = (patch: Partial<typeof p>) => onChange({ ...p, ...patch });
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="font-medium text-gray-900 text-sm">When recipients don't reply</h5>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Send reminder after (hours)</label>
          <input
            type="number"
            min={0}
            value={p.reminder_after_hours ?? 6}
            onChange={(e) => set({ reminder_after_hours: Number(e.target.value) || 0 })}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Escalate after (hours)</label>
          <input
            type="number"
            min={0}
            value={p.escalate_after_hours ?? 24}
            onChange={(e) => set({ escalate_after_hours: Number(e.target.value) || 0 })}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Reminder message</label>
        <input
          value={p.reminder_message || ''}
          onChange={(e) => set({ reminder_message: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          placeholder="Hi — still here? Whenever you have a moment."
        />
      </div>
    </div>
  );
};

// -- Add step row ----------------------------------------------------------

const AddStepRow: React.FC<{ onAdd: (type: StepType) => void }> = ({ onAdd }) => {
  const opts: { type: StepType; label: string; icon: React.ReactNode }[] = [
    { type: 'ask', label: 'Ask', icon: <MessageCircle size={13} /> },
    { type: 'send_template', label: 'Send', icon: <Send size={13} /> },
    { type: 'delay', label: 'Delay', icon: <Clock size={13} /> },
    { type: 'branch', label: 'Branch', icon: <GitBranch size={13} /> },
    { type: 'finish', label: 'Finish', icon: <Flag size={13} /> },
  ];
  return (
    <div className="bg-gray-50 rounded-xl border border-dashed border-gray-300 p-3 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-gray-500 inline-flex items-center gap-1.5"><Plus size={12} /> Add step</span>
      {opts.map((o) => (
        <button
          key={o.type}
          type="button"
          onClick={() => onAdd(o.type)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
        >
          {o.icon} {o.label}
        </button>
      ))}
    </div>
  );
};

// -- helpers ---------------------------------------------------------------

function generateStepId(existing: Step[], type: StepType): string {
  const prefix = type === 'ask' ? 'q' : type === 'send_template' ? 'send' : type === 'delay' ? 'wait' : type === 'branch' ? 'branch' : 'end';
  let n = 1;
  while (existing.some((s) => s.id === `${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

// ── Time / cron helpers ──────────────────────────────────────────────
// The backend stores schedule cron as a standard 5-field cron interpreted IN
// THE TRIGGER'S timezone field (see backend workflows/cron.py), so the time
// we write here is local to whatever IANA tz is on the trigger — no UTC
// conversion. Earlier versions of this file converted to UTC, which
// double-converted on the backend (cron stored as 11:10 + tz Asia/Karachi was
// read as 11:10 PKT, not the intended 16:10 PKT).

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Parse a stored cron ("MM HH …") into "HH:MM" — same tz semantics. */
function cronToLocalTime(cron?: string): string {
  if (!cron) return '09:00';
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 2) return '09:00';
  const mm = parseInt(parts[0], 10);
  const hh = parseInt(parts[1], 10);
  if (isNaN(mm) || isNaN(hh)) return '09:00';
  return `${pad2(hh)}:${pad2(mm)}`;
}

/** Day fields of a stored cron (day-of-month, month, day-of-week). */
function cronDayFields(cron?: string): string[] {
  const parts = (cron || '').trim().split(/\s+/);
  return parts.length === 5 ? parts.slice(2) : ['*', '*', '*'];
}

/** Write a "HH:MM" picker value into the cron, KEEPING its day fields — so
 *  changing the time of a weekly check-in never turns it into a daily one. */
function localTimeToCron(time: string, existing?: string): string {
  const [dom, month, dow] = cronDayFields(existing);
  if (!time || !time.includes(':')) return `0 9 ${dom} ${month} ${dow}`;
  const [hhStr, mmStr] = time.split(':');
  const hh = parseInt(hhStr, 10);
  const mm = parseInt(mmStr, 10);
  if (isNaN(hh) || isNaN(mm)) return `0 9 ${dom} ${month} ${dow}`;
  return `${mm} ${hh} ${dom} ${month} ${dow}`;
}

/** The single weekday ("0"–"6", Sunday = 0) a weekly cron runs on, else null. */
function cronWeekday(cron?: string): string | null {
  const [dom, month, dow] = cronDayFields(cron);
  if (dom !== '*' || month !== '*') return null;
  if (dow === '7') return '0';
  return /^[0-6]$/.test(dow) ? dow : null;
}

/** Same time, every day (weekday null) or every week on that weekday. */
function withWeekday(cron: string | undefined, weekday: string | null): string {
  const [mm, hh] = ((cron || '0 9').trim().split(/\s+/));
  return `${mm} ${hh} * * ${weekday ?? '*'}`;
}

/** "Every day" / "Every Friday" for the helper line. */
function scheduleLabel(cron?: string): string {
  const weekday = cronWeekday(cron);
  if (weekday !== null) return `Every ${WEEKDAYS[Number(weekday)]}`;
  const [dom, month, dow] = cronDayFields(cron);
  if (dom === '*' && month === '*' && dow === '*') return 'Every day';
  return `On schedule "${cron}"`;
}

/** Format "HH:MM" 24h as "9:00 AM" for the helper line. */
function humanTime(hhmm: string): string {
  const [hStr, mStr] = (hhmm || '09:00').split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${pad2(m)} ${period}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function makeBlankStep(type: StepType, id: string): Step {
  if (type === 'ask') {
    return { id, type, prompt: 'New question', response_type: 'text', timeout_hours: 6 } as Step;
  }
  if (type === 'send_template') {
    return { id, type, body: 'New message' } as Step;
  }
  if (type === 'delay') {
    return { id, type, duration_hours: 1 } as Step;
  }
  if (type === 'branch') {
    return { id, type, conditions: [], default_goto: '' } as Step;
  }
  return { id, type } as Step;
}

export default WorkflowEditor;
